import { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../context/AdminAuthContext';
import './Admin.css';

const emptyForm = { title: '', year: '', tags: '' };

export default function AdminGrants() {
  const { authFetch } = useAdminAuth();
  const [grants,    setGrants]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form,      setForm]      = useState(emptyForm);
  const [editId,    setEditId]    = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');

  const load = () => { 
    setLoading(true); 
    fetch('/api/grants')
      .then(r => {
        if (!r.ok) throw new Error(`HTTP error! status: ${r.status}`);
        return r.json();
      })
      .then(data => { 
        if (data && data.items) {
          setGrants(data.items);
        } else if (Array.isArray(data)) {
          setGrants(data);
        } else {
          setGrants([]);
        }
        setLoading(false); 
      })
      .catch((err) => {
        console.error("Fetch grants error in admin:", err);
        setGrants([]);
        setLoading(false);
      });
  };
  useEffect(() => { load(); }, []);

  const openNew  = () => { setForm(emptyForm); setEditId(null); setError(''); setShowModal(true); };
  const openEdit = gr  => { 
    setForm({ 
      ...emptyForm, 
      ...gr, 
      year: String(gr.year||''),
      tags: Array.isArray(gr.tags) ? gr.tags.join(', ') : (gr.tags || '')
    }); 
    setEditId(gr.id); setError(''); setShowModal(true); 
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setSaving(true); setError('');
    const body = { 
      ...form,
      tags: typeof form.tags === 'string' ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : (form.tags || [])
    };
    try {
      const url = editId ? `/api/grants/${editId}` : '/api/grants';
      const method = editId ? 'PUT' : 'POST';
      const res = await authFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      setShowModal(false); load();
    } catch { setError('Save failed.'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this grant?')) return;
    await authFetch(`/api/grants/${id}`, { method: 'DELETE' });
    load();
  };

  const field = (key, label, {type='text', placeholder=''}={}) => (
    <div className="admin-field" key={key}>
      <label>{label}</label>
      <input type={type} value={form[key]||''} onChange={e => setForm(f => ({...f, [key]: e.target.value}))} placeholder={placeholder} />
    </div>
  );

  return (
    <AdminLayout title="Awards & Grants" page="grants">
      <div className="admin-content">
        <div className="admin-page-header">
          <h2>Awards & Grants</h2>
          <button id="add-grant-btn" className="btn btn-primary btn-sm" onClick={openNew}>+ Add Award</button>
        </div>

        {loading ? (
          <div className="admin-loading">Loading…</div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr><th>Award / Grant Details</th><th>Year</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {grants.map(gr => (
                  <tr key={gr.id}>
                    <td className="admin-table__title" style={{ maxWidth: '600px', color: gr.title ? 'inherit' : 'var(--text-light)', fontStyle: gr.title ? 'normal' : 'italic' }}>
                      <div style={{ marginBottom: '0.3rem' }}>{gr.title || '(No title provided)'}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                        {(gr.tags || []).map(t => <span key={t} className="badge badge-navy" style={{ fontSize: '0.65rem', padding: '0.2rem 0.5rem' }}>{t}</span>)}
                      </div>
                    </td>
                    <td className="admin-table__year">{gr.year || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => openEdit(gr)}>✎ Edit</button>
                        <button className="btn btn-danger btn-sm"  onClick={() => handleDelete(gr.id)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="admin-modal-backdrop">
          <div className="admin-modal" style={{ maxWidth: 620 }}>
            <h3>{editId ? 'Edit Award/Grant' : 'Add Award/Grant'}</h3>
            <div className="admin-form">
              {field('title', 'Award / Grant Details *')}
              {field('year', 'Year (e.g. 2023-2025)', { placeholder: '2023-2025' })}
              {field('tags', 'Tags (comma separated for regions)', { placeholder: 'Southeast Asia, Indonesia, Global' })}
              {error && <p style={{ color: '#dc2626', fontSize: '0.82rem' }}>{error}</p>}
            </div>
            <div className="admin-modal-footer">
              <button className="btn btn-outline btn-sm" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
